import { DateService } from "@/core/services/Date.service";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { FilterDateRange } from "../interfaces";
import { AppConfig } from "@/infrastructure/services/config";

const dateService = new DateService(new DateAdapter());

const getCurrentDateIso = (): string => dateService.getCurrentDate().toISOString();

/** Always reflects the current system date (not module-load time). */
export const getCurrentDateFormatted = (): string =>
  dateService.format(getCurrentDateIso(), "YYYY-MM-DD");

/** Buffer added to check "to" date so upcoming/future checks are included in preset ranges. */
export const CHECK_TO_DATE_FUTURE_BUFFER_MONTHS = 1;

/**
 * Calculates the from date based on the number of months to subtract from current date
 */
export const calculateFromDateByMonths = (months: number): string => {
  return dateService.format(
    dateService.subtract(getCurrentDateIso(), months, "month").toISOString(),
    "YYYY-MM-DD"
  );
};

/**
 * Calculates the to date based on the number of months to add to current date
 */
export const calculateToDateByMonths = (months: number): string => {
  return dateService.format(
    dateService.add(getCurrentDateIso(), months, "month").toISOString(),
    "YYYY-MM-DD"
  );
};

export const DATE_CONSTANTS = {
  get currentDate(): string {
    return getCurrentDateIso();
  },
  get currentDateFormatted(): string {
    return getCurrentDateFormatted();
  },
  /** Check date "to" default: today + 1 month (includes future checks). */
  get checkToDateDefault(): string {
    return calculateToDateByMonths(CHECK_TO_DATE_FUTURE_BUFFER_MONTHS);
  },
  defaultFromDate: AppConfig.getInstance().FILTER_DATE_CONFIG.DEFAULT_FROM_DATE,
  lastAvailableDate: AppConfig.getInstance().FILTER_DATE_CONFIG.LAST_AVAILABLE_DATE,
  get checkFromDateDefaultMinValue(): string {
    return AppConfig.getInstance().FILTER_DATE_CONFIG.LAST_AVAILABLE_DATE;
  },
  get checkFromDateDefaultMaxValue(): string {
    return getCurrentDateFormatted();
  },
  /** Max selectable check "to" date — today + future buffer. */
  get checkToDateDefaultMaxValue(): string {
    return calculateToDateByMonths(CHECK_TO_DATE_FUTURE_BUFFER_MONTHS);
  },
  get invoiceFromDateDefaultMinValue(): string {
    return AppConfig.getInstance().FILTER_DATE_CONFIG.LAST_AVAILABLE_DATE;
  },
  get invoiceFromDateDefaultMaxValue(): string {
    return getCurrentDateFormatted();
  },
};

/**
 * Converts a FilterDateRange enum value to actual date range (fromDate, toDate).
 * When `toDateOffsetMonths` is set (e.g. 1 for check dates), toDate is today + that many months
 * so future-dated records are included.
 */
export const convertDateRangeToDates = (
  dateRange: FilterDateRange | null,
  defaultMonths: number,
  toDateOffsetMonths = 0
): { fromDate: string | null; toDate: string | null } => {
  if (!dateRange) {
    return { fromDate: null, toDate: null };
  }

  const toDate =
    toDateOffsetMonths > 0
      ? calculateToDateByMonths(toDateOffsetMonths)
      : getCurrentDateFormatted();

  if (dateRange === FilterDateRange.THIS_MONTH) {
    return {
      fromDate: dateService.format(dateService.startOfMonth(getCurrentDateIso()).toISOString(), "YYYY-MM-DD"),
      toDate,
    };
  }

  let months: number;
  switch (dateRange) {
    case FilterDateRange.LAST_1_MONTH:
      months = 1;
      break;
    case FilterDateRange.LAST_3_MONTHS:
      months = 3;
      break;
    case FilterDateRange.LAST_6_MONTHS:
      months = 6;
      break;
    case FilterDateRange.LAST_12_MONTHS:
      months = 12;
      break;
    default:
      months = defaultMonths;
  }

  return {
    fromDate: calculateFromDateByMonths(months),
    toDate,
  };
};

/**
 * Gets default check date range based on workspace settings
 */
export const getDefaultCheckDateRange = (
  defaultMonths: number
): { fromDate: string; toDate: string; preset: string | null } => {
  const fromDate = calculateFromDateByMonths(defaultMonths);
  return {
    fromDate,
    toDate: calculateToDateByMonths(CHECK_TO_DATE_FUTURE_BUFFER_MONTHS),
    preset: null, // Will be set by mapMonthsToFilterDateRange
  };
};

